import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminService } from '../../services/AdminService/admin';
import {
  AdminExportRequestResponse,
  ExportRequestStatsResponse,
  ExportRequestStatus,
} from '../../models/export-request.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { statusColor } from '../../shared/status-colors';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';

const STATUSES: ExportRequestStatus[] = ['PREPARING', 'PENDING', 'ACCEPTED', 'DECLINED', 'TRACE_FAILED'];

const STATUS_ICONS: Record<ExportRequestStatus, string> = {
  PREPARING: 'hourglass_empty',
  PENDING: 'schedule',
  ACCEPTED: 'check_circle',
  DECLINED: 'cancel',
  TRACE_FAILED: 'error',
};

function titleCase(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

@Component({
  selector: 'app-platform-export-requests',
  imports: [CommonModule, FormsModule, RouterLink, StatusBadge, ErrorBanner, EmptyState, FilterSelect, PageSizeSelect],
  templateUrl: './platform-export-requests.html',
  styleUrl: './platform-export-requests.scss',
})
export class PlatformExportRequests implements OnInit {
  statusOptions: FilterOption[] = STATUSES.map((s) => ({ value: s, label: titleCase(s) }));
  statusFilter: ExportRequestStatus | '' = '';

  stats = signal<ExportRequestStatsResponse | null>(null);
  statsError = signal('');

  requests = signal<AdminExportRequestResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);
  totalPages = signal(1);
  totalCount = signal(0);

  constructor(private admin: AdminService) {}

  get rangeStart(): number {
    return this.totalCount() === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount());
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages();
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  ngOnInit(): void {
    this.loadStats();
    this.load();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.load();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.load();
  }

  get statusBreakdown(): { status: ExportRequestStatus; label: string; icon: string; count: number }[] {
    const byStatus = this.stats()?.byStatus;
    return STATUSES.map((status) => ({
      status,
      label: titleCase(status),
      icon: STATUS_ICONS[status],
      count: byStatus?.[status] ?? 0,
    }));
  }

  cardColor(status: ExportRequestStatus): string {
    return statusColor(status);
  }

  loadStats(): void {
    this.statsError.set('');

    this.admin.exportRequestStats().subscribe({
      next: (res) => this.stats.set(res.data),
      error: (err: HttpErrorResponse) => {
        this.statsError.set(extractErrorMessage(err, 'Failed to load export request stats.'));
      },
    });
  }

  onFilterChange(): void {
    this.page.set(0);
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.admin.listExportRequests({ status: this.statusFilter || undefined }, this.page(), this.pageSize()).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.requests.set(res.data);
        this.totalPages.set(res.page?.total_pages ?? 1);
        this.totalCount.set(res.page?.count ?? res.data.length);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load export requests.'));
      },
    });
  }
}
